import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth, googleProvider } from '../services/firebase';

const API_BASE = '/api';

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

// Detect mobile/tablet
const isMobile = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Handle redirect result on mount (for mobile)
  useEffect(() => {
    const processRedirect = async () => {
      try {
        const result = await getRedirectResult(auth);
        
        if (result?.user) {
          const email = result.user.email;
          
          if (!email.endsWith('@glossnail.bar')) {
            await signOut(auth);
            setError('Only @glossnail.bar emails are allowed');
            setLoading(false);
            return;
          }

          try {
            const response = await fetch(`${API_BASE}/check-access.php`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email }),
              credentials: 'include'
            });
            
            const data = await response.json();
            
            if (data.allowed) {
              setUser({
                email: result.user.email,
                displayName: result.user.displayName,
                photoURL: result.user.photoURL,
                uid: result.user.uid
              });
              setAccess({
                sections: data.sections || [],
                role: data.role || 'none'
              });
            } else {
              await signOut(auth);
              setError(data.error || 'Access denied');
            }
          } catch (err) {
            console.error('Access check error:', err);
            setError('Failed to verify access');
          }
        }
      } catch (err) {
        console.error('Redirect error:', err);
        if (err.code === 'auth/unauthorized-domain') {
          setError('Domain not authorized. Please contact admin.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    processRedirect();
  }, []);

  // Listen for auth changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser && !user) {
        const email = firebaseUser.email;
        
        if (!email.endsWith('@glossnail.bar')) {
          await signOut(auth);
          setUser(null);
          setAccess(null);
          setError('Only @glossnail.bar emails are allowed');
          setLoading(false);
          return;
        }

        try {
          const response = await fetch(`${API_BASE}/check-access.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
            credentials: 'include'
          });
          
          const data = await response.json();
          
          if (data.allowed) {
            setUser({
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
              uid: firebaseUser.uid
            });
            setAccess({
              sections: data.sections || [],
              role: data.role || 'none'
            });
          } else {
            await signOut(auth);
            setUser(null);
            setAccess(null);
            setError(data.error || 'Access denied');
          }
        } catch (err) {
          console.error('Access check error:', err);
          setError('Failed to verify access');
        }
      } else if (!firebaseUser) {
        setUser(null);
        setAccess(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const login = async () => {
    try {
      setError(null);
      setLoading(true);
      
      if (isMobile()) {
        // Try redirect flow for mobile
        await signInWithRedirect(auth, googleProvider);
      } else {
        // Use popup for desktop
        const result = await signInWithPopup(auth, googleProvider);
        
        if (result.user) {
          const email = result.user.email;
          
          if (!email.endsWith('@glossnail.bar')) {
            await signOut(auth);
            setError('Only @glossnail.bar emails are allowed');
            setLoading(false);
            return;
          }
          
          const response = await fetch(`${API_BASE}/check-access.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
            credentials: 'include'
          });
          
          const data = await response.json();
          
          if (data.allowed) {
            setUser({
              email: result.user.email,
              displayName: result.user.displayName,
              photoURL: result.user.photoURL,
              uid: result.user.uid
            });
            setAccess({
              sections: data.sections || [],
              role: data.role || 'none'
            });
          } else {
            await signOut(auth);
            setError(data.error || 'Access denied');
          }
        }
        setLoading(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      setLoading(false);
      
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Login was cancelled');
      } else if (err.code === 'auth/popup-blocked') {
        setError('Popup blocked. Please allow popups or use desktop.');
      } else if (err.code === 'auth/unauthorized-domain') {
        setError('Domain not authorized');
      } else if (err.code === 'auth/operation-not-allowed') {
        setError('Google sign-in not enabled');
      } else {
        setError(err.message || 'Failed to login');
      }
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setAccess(null);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const value = {
    user,
    access,
    loading,
    error,
    isMobile: isMobile(),
    login,
    logout,
    isAuthenticated: !!user && !!access && access.sections?.length > 0
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
